import { MathUtils } from 'three';
import { transform } from 'ol/proj';
import { toast } from '../../../app';
import {
  AerialViewState,
  initialSceneState,
  SceneCameraState
} from '../../../redux/camera-slice';
import { t } from '../../../localization';

export const parseURLParams = (url) => {
  const hash = new URL(url).hash.substring(1);
  if (hash === "") return;

  const urlParams = new URLSearchParams(hash);
  const parameters = Object.fromEntries(urlParams);

  return parameters;
};

export const getModifiedAerialState = (aerialState: AerialViewState, params) => {
  let modified = {...aerialState};

  if (("lon" in params) && ("lat" in params)) {
    const lat = parseFloat(params.lat);
    const lon = parseFloat(params.lon);

    if (isNaN(lat) || isNaN(lon)) {
      toast.warning(t("toast.parse-error-lat-lon"));
    } else {
      modified.center = transform([lon, lat], 'EPSG:4326', 'EPSG:3857');
    }
  }

  if ("zoom" in params) {
    const zoom = parseInt(params.zoom);

    if (isNaN(zoom)) {
      toast.warning(t("toast.parse-error-aerial-zoom"));
    } else {
      modified.zoom = zoom;
    }
  }

  if ("rotation" in params) {
    const rotation = parseFloat(params.rotation);

    if (isNaN(rotation)) {
      toast.warning(t("toast.parse-error-aerial-rotate"));
    } else {
      modified.rotation = MathUtils.degToRad(rotation);
    }
  }

  return modified;
};

export const getModifiedSceneState = (sceneState: SceneCameraState, params) => {
  let modified = {...sceneState};

  if (("px" in params) && ("py" in params) && ("pz" in params)) {
    if (("ox" in params) && ("oy" in params) && ("oz" in params)) {
      const ox = parseFloat(params.ox);
      const oy = parseFloat(params.oy);
      const oz = parseFloat(params.oz);

      const px = parseFloat(params.px);
      const py = parseFloat(params.py);
      const pz = parseFloat(params.pz);

      const hasError = isNaN(ox)
        || isNaN(oy)
        || isNaN(oz)
        || isNaN(px)
        || isNaN(py)
        || isNaN(pz);

      if (hasError) {
        toast.warning(t("toast.parse-error-3d-mode"));
      } else {
        return {
          ...initialSceneState,
          orbit: [ox, oy, oz],
          pivot: [px, py, pz]
        };
      }
    }
  }

  if (("x" in params) && ("y" in params) && ("z" in params)) {
    const x = parseFloat(params.x);
    const y = parseFloat(params.y);
    const z = parseFloat(params.z);

    const hasError = isNaN(x)
      || isNaN(y)
      || isNaN(z);

    if (hasError) {
      toast.warning(t("toast.parse-error-xyz"));
    } else {
      // Clear all values and update lookat
      modified = {
        ...initialSceneState,
        lookat: [x,y,z]
      };
    }
  }

  if ("cam" in params) {
    // Set camera and clear orbit values
    modified.camera = params.cam;
    modified.orbit = null;
    modified.pivot = null;
  }

  if (("theta" in params) && ("phi" in params)) {
    const theta = parseFloat(params.theta);
    const phi = parseFloat(params.phi);

    if (isNaN(theta) || isNaN(phi)) {
      toast.warning(t("toast.parse-error-angle"));
    } else {
      modified.angles = [
        MathUtils.degToRad(theta),
        MathUtils.degToRad(phi)
      ];
    }
  }

  if ("fov" in params) {
    const fov = parseFloat(params.fov);

    if (isNaN(fov)) {
      toast.warning(t("toast.parse-error-camera-fov"));
    } else {
      modified.fov = fov;
    }
  }

  return modified;
};
